import { inject } from 'vue'
import type { App, InjectionKey, Plugin } from 'vue'
import {
  getDevices,
  getUserDevices,
  getDevice,
  postRegisterDevice,
  postUnregisterDevice,
  patchUpdateDevice,
  postShareDevice,
  getDeviceCollaborators,
  deleteDeviceCollaborator,
  getDeviceConfig,
  postDeviceConfig,
  getModelsMetaData,
  patchDeviceSubscription,
  getDeviceStateStream as streamDeviceState
} from './services/device'
import { getCSV, getNotificationsCSV } from './services/data-export'
import {
  getLatest as getTelemetryLatest,
  getLatestCurrentReadings as getCurrentReadingsLatest,
  getGraphData as getTelemetryGraphData,
  getAverages as getTelemetryAverages,
  getPercentChanged as getTelemetryPercentChanged,
  getLatestTelemetryStream as streamTelemetryLatest,
  getCurrentReadingsStream as streamCurrentReadings
} from './services/telemetry'
import { getMetadata } from './services/user-metadata'
import {
  getSubscriptionSKUMetadata,
  getUserSubscriptions,
  getUserPermissions,
  postValidateSubscriptionCode,
  patchCancelSubscription,
  postBillingInfoPurchaseOrder,
  postBillingInfoCreditCard,
  getDelegoToken
} from './services/user-subscription'
import {
  getUser,
  patchUpdateUser,
  postResetPassword,
  patchUserFullName,
  patchUserSettings,
  getUserSettings,
  postUserSettings
} from './services/user'
import {
  getAlert,
  getAlerts,
  postAlert,
  patchAlert,
  deleteAlert,
  getAlertNotifications,
  patchClearNotification,
  postClearAllNotifications,
  postClearAllDeviceNotifications,
  deleteAlertNotification
} from './services/alert'
import { deviceType } from './services/utils'
import {
  getWidgets,
  postWidget,
  patchWidget,
  deleteWidget,
  getLayout,
  postLayout
} from './services/dashboard'
import { getAccountsForAdminPanel, healthChecker } from './services/admin-panel'
import {
  getLatestStudyRegistrations,
  getLatestDeviceStudy,
  getFileImportCount,
  getTotalStorageUsed
} from './services/studies'
import { createEmulator, getAllEmulators } from './services/developer-panel'

const buildApi = () => ({
  deviceType,
  getDevices,
  getUserDevices,
  getDevice,
  postRegisterDevice,
  patchUpdateDevice,
  postShareDevice,
  getDeviceCollaborators,
  deleteDeviceCollaborator,
  getDeviceConfig,
  postDeviceConfig,
  getModelsMetaData,
  patchDeviceSubscription,
  postUnregisterDevice,
  getCSV,
  getNotificationsCSV,
  getTelemetryLatest,
  getCurrentReadingsLatest,
  getTelemetryGraphData,
  getTelemetryAverages,
  getTelemetryPercentChanged,
  streamTelemetryLatest,
  streamCurrentReadings,
  streamDeviceState,
  getMetadata,
  getUser,
  getUserPermissions,
  patchUpdateUser,
  postResetPassword,
  patchUserFullName,
  patchUserSettings,
  getSubscriptionSKUMetadata,
  getUserSubscriptions,
  postValidateSubscriptionCode,
  patchCancelSubscription,
  postBillingInfoPurchaseOrder,
  postBillingInfoCreditCard,
  getDelegoToken,
  getUserSettings,
  postUserSettings,
  getAlert,
  getAlerts,
  postAlert,
  patchAlert,
  deleteAlert,
  getWidgets,
  postWidget,
  patchWidget,
  deleteWidget,
  getLayout,
  postLayout,
  getAlertNotifications,
  patchClearNotification,
  postClearAllNotifications,
  postClearAllDeviceNotifications,
  deleteAlertNotification,
  getAccountsForAdminPanel,
  healthChecker,
  getLatestStudyRegistrations,
  getLatestDeviceStudy,
  getFileImportCount,
  getTotalStorageUsed,
  createEmulator,
  getAllEmulators
})

export type ApiService = ReturnType<typeof buildApi>

let instance: ApiService

export const getInstance = () => instance

const createApi = (): ApiService => {
  if (instance) return instance
  instance = buildApi()
  return instance
}

const apiKey: InjectionKey<ApiService> = Symbol('api')

export const useApi = (): ApiService => {
  const api = inject(apiKey)
  if (!api) throw new Error('API plugin not installed')
  return api
}

const ApiPlugin: Plugin = {
  install(app: App) {
    const api = createApi()
    app.config.globalProperties.$api = api
    app.provide(apiKey, api)
  }
}

export default ApiPlugin
